import type { ApiError } from '@/lib/apiClient';
import { Temper } from '@/types/frontend-types';
import { resolveSessionMutationValidationErrorBody } from './session-mutation-validation-errors';

export const SESSION_PLAN_INSUFFICIENT_MESSAGE =
  "Your plan isn't sufficient for this action.  \nYou must be at least **Conspire** in order to earn your privacy.";
export const SESSION_ACCESS_DENIED_MESSAGE = 'Access denied to this session';
export const SESSION_MUTATION_GENERIC_FALLBACK_BODY =
  'Action denied by the CABAL.  \n Revisit your input.';
export const SESSION_START_DENIED_BODY =
  'Session start denied by the CABAL.  \n Revisit your input.';
const MESSAGE_MODAL_ACKNOWLEDGE_TEXT = 'Acknowledge';
const AUTHENTICATION_REQUIRED_BODY = 'Authentication is required for this action';
const ACCESS_DENIED_FALLBACK_BODY = 'Access denied';

enum SessionMutationHttpStatus {
  Unauthorized = 401,
  Forbidden = 403,
}

enum SessionMutationErrorCode {
  InsufficientPlan = 'INSUFFICIENT_PLAN',
}

enum SessionMutationModalTitle {
  AccessDenied = 'ACCESS DENIED',
  DeniedByCabal = 'DENIED BY THE CABAL',
}

interface ShowMessageModalInput {
  temper: Temper;
  title: string;
  hackerAllCaps?: boolean;
  body?: string;
  secondaryButtonText?: string;
}

interface HandleSessionMutationErrorInput {
  error: unknown;
  showMessageModal: (input: ShowMessageModalInput) => void;
  // Body shown for a non-401/403 denial when the BE sends no curated
  // `userMessage`. Defaults to the generic "Action denied" copy; the
  // session-creation flow passes `SESSION_START_DENIED_BODY`.
  fallbackBody?: string;
}

interface ResolvedSessionMutationError {
  title: SessionMutationModalTitle;
  body: string;
}

interface ResolveSessionMutationErrorInput {
  error: ApiError;
  fallbackBody: string;
}

type CuratedSessionMutationErrorRule = (error: ApiError) => boolean;
type ForbiddenBodyResolver = (error: ApiError) => string | null;
type SessionMutationStatusResolver = (
  input: ResolveSessionMutationErrorInput,
) => ResolvedSessionMutationError;

const CURATED_STATUS_CODES = new Set<number>([
  SessionMutationHttpStatus.Unauthorized,
  SessionMutationHttpStatus.Forbidden,
]);

const FORBIDDEN_CODE_BODIES = new Map<string, string>([
  [SessionMutationErrorCode.InsufficientPlan, SESSION_PLAN_INSUFFICIENT_MESSAGE],
]);

const FORBIDDEN_MESSAGE_BODIES = new Map<string, string>([
  [SESSION_ACCESS_DENIED_MESSAGE, SESSION_ACCESS_DENIED_MESSAGE],
]);

function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof Error &&
    'statusCode' in error &&
    typeof error.statusCode === 'number'
  );
}

function asApiError(error: unknown): ApiError | null {
  return isApiError(error) ? error : null;
}

export function isForbiddenApiError(error: unknown): boolean {
  const apiError = asApiError(error);
  return apiError?.statusCode === SessionMutationHttpStatus.Forbidden;
}

function hasCuratedStatusCode(error: ApiError): boolean {
  return CURATED_STATUS_CODES.has(error.statusCode);
}

function hasUserMessage(error: ApiError): boolean {
  return Boolean(error.userMessage);
}

function hasValidationErrorBody(error: ApiError): boolean {
  return resolveApiValidationErrorBody(error) != null;
}

const CURATED_ERROR_RULES: CuratedSessionMutationErrorRule[] = [
  hasCuratedStatusCode,
  hasUserMessage,
  hasValidationErrorBody,
];

/**
 * Whether `handleSessionMutationError` would render a CURATED message for this
 * error (a deliberate, user-meaningful one) rather than falling back to its
 * generic "denied by the CABAL" copy.
 *
 * Curated cases:
 * - 401 / 403 (auth + plan-aware messaging)
 * - a backend-supplied `userMessage` (the BE explicitly chose this text)
 * - a VALIDATION error that resolves to per-field issue text
 *
 * A bare server error (e.g. a 500 with no `userMessage`) is NOT curated — the
 * caller can choose its own generic fallback instead of the handler's.
 */
export function isCuratedSessionMutationError(error: unknown): boolean {
  const apiError = asApiError(error);
  return apiError ? CURATED_ERROR_RULES.some((rule) => rule(apiError)) : false;
}

export function resolveApiValidationErrorBody(error: unknown): string | null {
  const apiError = asApiError(error);
  return apiError ? resolveSessionMutationValidationErrorBody(apiError) : null;
}

/**
 * Render a session-mutation error to the user.
 *
 * Returns true when an `ApiError` was handled (modal shown). Returns false for
 * non-API errors so callers can decide their own non-API fallback.
 *
 * Display rules:
 * - 401: hard-coded "auth required" copy.
 * - 403: plan-aware message (`INSUFFICIENT_PLAN` code) → curated `userMessage`
 *   from BE → existing 403 message → "Access denied".
 * - any other status: BE's curated `userMessage` if present; otherwise
 *   `input.fallbackBody` (defaults to the generic
 *   `SESSION_MUTATION_GENERIC_FALLBACK_BODY`; the session-creation flow passes
 *   `SESSION_START_DENIED_BODY`). Raw BE `message` is never surfaced — it can
 *   be developer-facing technical text.
 */
export function handleSessionMutationError(input: HandleSessionMutationErrorInput): boolean {
  if (!isApiError(input.error)) {
    return false;
  }

  const resolved = resolveSessionMutationError({
    error: input.error,
    fallbackBody: input.fallbackBody ?? SESSION_MUTATION_GENERIC_FALLBACK_BODY,
  });

  input.showMessageModal({
    temper: Temper.Bad,
    title: resolved.title,
    hackerAllCaps: true,
    body: resolved.body,
    secondaryButtonText: MESSAGE_MODAL_ACKNOWLEDGE_TEXT,
  });
  return true;
}

function resolveUnauthorizedMutationError(): ResolvedSessionMutationError {
  return {
    title: SessionMutationModalTitle.AccessDenied,
    body: AUTHENTICATION_REQUIRED_BODY,
  };
}

function resolveForbiddenMutationError(
  input: ResolveSessionMutationErrorInput,
): ResolvedSessionMutationError {
  return {
    title: SessionMutationModalTitle.AccessDenied,
    body: resolveForbiddenBody(input.error),
  };
}

function resolveDefaultMutationError(
  input: ResolveSessionMutationErrorInput,
): ResolvedSessionMutationError {
  const validationBody = resolveApiValidationErrorBody(input.error);
  return {
    title: SessionMutationModalTitle.DeniedByCabal,
    body: validationBody ?? input.error.userMessage ?? input.fallbackBody,
  };
}

const SESSION_MUTATION_STATUS_RESOLVERS = new Map<
  number,
  SessionMutationStatusResolver
>([
  [
    SessionMutationHttpStatus.Unauthorized,
    resolveUnauthorizedMutationError,
  ],
  [
    SessionMutationHttpStatus.Forbidden,
    resolveForbiddenMutationError,
  ],
]);

function resolveSessionMutationError(
  input: ResolveSessionMutationErrorInput,
): ResolvedSessionMutationError {
  const resolver =
    SESSION_MUTATION_STATUS_RESOLVERS.get(input.error.statusCode) ??
    resolveDefaultMutationError;
  return resolver(input);
}

function resolveCodeForbiddenBody(error: ApiError): string | null {
  return error.code ? FORBIDDEN_CODE_BODIES.get(error.code) ?? null : null;
}

function resolveUserMessageForbiddenBody(error: ApiError): string | null {
  return error.userMessage ? error.userMessage : null;
}

function resolveMatchingMessageForbiddenBody(error: ApiError): string | null {
  return FORBIDDEN_MESSAGE_BODIES.get(error.message) ?? null;
}

/**
 * The rules that produce text the BACKEND chose for this user: the copy mapped
 * to a known error code, then the BE's own `userMessage`. Everything after them
 * is this frontend guessing.
 */
const CURATED_BODY_RESOLVERS: ForbiddenBodyResolver[] = [
  resolveCodeForbiddenBody,
  resolveUserMessageForbiddenBody,
];

const FORBIDDEN_BODY_RESOLVERS: ForbiddenBodyResolver[] = [
  ...CURATED_BODY_RESOLVERS,
  resolveMatchingMessageForbiddenBody,
];

function isResolvedBody(body: string | null): body is string {
  return body != null;
}

function resolveCuratedBody(error: ApiError): string | null {
  return CURATED_BODY_RESOLVERS
    .map((resolver) => resolver(error))
    .find(isResolvedBody) ?? null;
}

/**
 * The body the BACKEND curated for this error, or null when it curated none —
 * the `INSUFFICIENT_PLAN` plan copy, or the BE's own `userMessage`.
 *
 * `handleSessionMutationError` renders the same text through the blocking
 * message modal. A surface that reports failures INLINE instead has no modal to
 * carry it, and a locally-derived "action failed" sentence would
 * drop the one thing that tells the user WHY — which plan limit they hit. Those
 * callers resolve the curated body first and keep their own per-class sentence
 * as the fallback, so the raw developer-facing `message` is still never shown.
 */
export function resolveSessionMutationCuratedBody(error: unknown): string | null {
  const apiError = asApiError(error);
  return apiError ? resolveCuratedBody(apiError) : null;
}

function resolveForbiddenBody(error: ApiError): string {
  const ruleBody = FORBIDDEN_BODY_RESOLVERS
    .map((resolver) => resolver(error))
    .find(isResolvedBody);
  const fallbackBody = ruleBody ?? error.message;
  return fallbackBody || ACCESS_DENIED_FALLBACK_BODY;
}
